import { BrandLogo } from '@bia-auction/ui';
import { t } from '@bia-auction/shared-i18n';
import { Link } from '@tanstack/react-router';
import { Mail, MapPin, Phone } from 'lucide-react';
import type { ReactNode } from 'react';
import { SocialLinks } from './SocialLinks';
import { useAuth } from '../lib/auth';
import { BRAND } from '../lib/config';
import { COMPANY, CONTACT, CORPORATE_LINKS, LEGAL_URL, SITE_URL, telHref } from '../lib/site';

const linkClass =
  'text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm';

function ColumnHeading({ id, children }: { id?: string; children: ReactNode }) {
  return (
    <h2
      id={id}
      className="mb-4 font-head text-sm font-semibold uppercase tracking-wider text-muted-foreground"
    >
      {children}
    </h2>
  );
}

/**
 * Site footer, mirroring the main brand site (biaoperasyonel.com): brand blurb +
 * social on the left, then quick access (in-app), corporate (brand site) and
 * contact columns, over a copyright bar.
 *
 * Like {@link SiteHeader} this is a **dark scope** (`data-theme="dark"`, docs/15 §5)
 * rather than a page-level theme switch, so it stays navy on the light routes.
 * Every configurable item is dropped when its value is empty (`lib/site.ts`).
 */
export function SiteFooter() {
  const { state } = useAuth();
  // Rendered client-side, so "now" is the visitor's clock — a static build stays correct
  // across a year boundary without a redeploy.
  const year = new Date().getFullYear();

  return (
    <footer data-theme="dark" className="bg-surface text-foreground">
      <div className="mx-auto grid max-w-5xl gap-10 px-4 py-12 sm:grid-cols-2 lg:grid-cols-4">
        <div className="lg:pr-6">
          <BrandLogo variant="white" alt={BRAND} className="h-10" />
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
            {t('bidder.footer.tagline')}
          </p>
          <div className="mt-6">
            <SocialLinks />
          </div>
        </div>

        <nav aria-labelledby="footer-quick">
          <ColumnHeading id="footer-quick">{t('bidder.footer.quickLinks')}</ColumnHeading>
          <ul className="space-y-3 text-sm">
            <li>
              <Link to="/" className={linkClass}>
                {t('bidder.nav.auctions')}
              </Link>
            </li>
            {state.authenticated ? (
              <>
                <li>
                  <Link to="/me/bids" className={linkClass}>
                    {t('bidder.nav.myBids')}
                  </Link>
                </li>
                <li>
                  <Link to="/membership" className={linkClass}>
                    {t('bidder.nav.membership')}
                  </Link>
                </li>
              </>
            ) : (
              <>
                <li>
                  <Link to="/login" className={linkClass}>
                    {t('bidder.nav.login')}
                  </Link>
                </li>
                <li>
                  <Link to="/register" className={linkClass}>
                    {t('bidder.nav.register')}
                  </Link>
                </li>
              </>
            )}
          </ul>
        </nav>

        {SITE_URL && (
          <nav aria-labelledby="footer-corporate">
            <ColumnHeading id="footer-corporate">{t('bidder.footer.corporate')}</ColumnHeading>
            <ul className="space-y-3 text-sm">
              {CORPORATE_LINKS.map(({ key, href }) => (
                <li key={key}>
                  <a href={href} target="_blank" rel="noreferrer noopener" className={linkClass}>
                    {t(key)}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        )}

        <div>
          <ColumnHeading>{t('bidder.footer.contact')}</ColumnHeading>
          <ul className="space-y-3 text-sm text-muted-foreground">
            {CONTACT.phone && (
              <li>
                <a href={telHref} className="flex items-start gap-2 hover:text-foreground">
                  <Phone aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-tx-brand" />
                  {CONTACT.phone}
                </a>
              </li>
            )}
            {CONTACT.email && (
              <li>
                <a
                  href={`mailto:${CONTACT.email}`}
                  className="flex items-start gap-2 hover:text-foreground"
                >
                  <Mail aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-tx-brand" />
                  {CONTACT.email}
                </a>
              </li>
            )}
            {CONTACT.address && (
              <li className="flex items-start gap-2">
                <MapPin aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-tx-brand" />
                <span>{CONTACT.address}</span>
              </li>
            )}
          </ul>
        </div>
      </div>

      <div className="border-t border-border bg-sunken">
        <div className="mx-auto flex max-w-5xl flex-col gap-2 px-4 py-4 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <p>{t('bidder.footer.rights', { year, company: COMPANY })}</p>
          {SITE_URL && (
            <a href={LEGAL_URL} target="_blank" rel="noreferrer noopener" className={linkClass}>
              {t('bidder.site.legal')}
            </a>
          )}
        </div>
      </div>
    </footer>
  );
}
